import axios from 'axios';
import { normalizeError } from 'api/utils/normalizeError';
import { refreshAccessToken, refreshIfExpiring } from 'api/utils/refreshTokenUtils';
import { LOGIN } from 'constants/routes';
import { useAuthStore } from 'stores/authStore';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import history from 'utils/history';
import env from '../config';

const apiClient = axios.create({
  baseURL: env.apiBaseUrlRambla,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 20000,
  withCredentials: true,
});

apiClient.interceptors.request.use(
  async (config) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      try {
        await refreshIfExpiring({ expiresWithinSeconds: 10 });
      } catch {
        // Let the original request proceed
      }
    }

    const freshAccessToken = useAuthStore.getState().accessToken;
    if (freshAccessToken) {
      config.headers.Authorization = `Bearer ${freshAccessToken}`;
    }

    const { selectedBrandUid, selectedRegionUid } = useSelectedProfileStore.getState();
    if (selectedBrandUid && selectedRegionUid) {
      config.headers.Brand = selectedBrandUid;
      config.headers.Region = selectedRegionUid;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const access = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return apiClient(originalRequest);
      } catch {
        useAuthStore.getState().clearAuth();
        if (history.location.pathname !== LOGIN) {
          history.push(LOGIN);
        }
      }
    }

    return Promise.reject(normalizeError(error));
  },
);

export const apiRequest = async (method, url, data, config) => {
  const response = await apiClient.request({
    method,
    url,
    data,
    ...config,
  });

  return response.data;
};

export default apiClient;
